import { isStringArray } from '@portals/shared/common/utils';
import { hasOwnProperty } from '@portals/shared-frontend/utils';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import { isSignedInWithAzure, msalInstance } from '@/auth/azure';
import { isLoggedInWithCognito } from '@/auth/cognito';
import { CELEBRUS_LOGIN_STATE_KEY, DNB_TENANT_ID } from '@/constants';
import AuthContext from '@/hooks/AuthContext';

export default function AuthContextProvider({ children }: { children?: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState<'azure' | 'cognito'>();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [isInternal, setIsInternal] = useState<boolean>();

  const checkIsLoggedIn = useCallback(async () => {
    setLoading(true);
    const [_isLoggedInWithAzure, _isLoggedInWithCognito] = await Promise.all([
      isSignedInWithAzure(),
      isLoggedInWithCognito(),
    ]);
    if (_isLoggedInWithAzure) {
      setIsLoggedIn('azure');
    } else if (_isLoggedInWithCognito) {
      setIsLoggedIn('cognito');
    } else {
      setIsLoggedIn(undefined);
    }
    setLoading(false);
  }, [setLoading, setIsLoggedIn]);

  useEffect(() => {
    void checkIsLoggedIn();
  }, [checkIsLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      sessionStorage.removeItem(CELEBRUS_LOGIN_STATE_KEY);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const account = msalInstance.getAllAccounts()[0];
    if (account) {
      const idTokenClaims = account.idTokenClaims;
      if (idTokenClaims) {
        // Determine if the user is internal based on tenant ID (tid)
        setIsInternal(idTokenClaims.tid === DNB_TENANT_ID);

        // Extract roles from the token
        if (hasOwnProperty(idTokenClaims, 'roles')) {
          const idTokenRoles = idTokenClaims['roles'];
          if (isStringArray(idTokenRoles)) {
            setRoles(idTokenRoles);
          }
        }
      }
    }
  }, [isLoggedIn]);

  return (
    <AuthContext.Provider
      value={{
        isSignedIn: !!isLoggedIn,
        isLoading: loading,
        refresh: checkIsLoggedIn,
        roles: roles,
        isInternal: isInternal ?? false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
